import { shortId } from '../../components/format.ts';
import type { DiscoveryProgressResponse, DiscoveryRecords } from './types.ts';
import { attemptStatusLabel } from './model.ts';
import { authorizedScopeSummary } from '../authorization/scope.ts';
import {
  coverageForEvidence,
  distinctEvidence,
  formatEvidenceLine,
  findingTaskLane,
  findingId,
  findingTitle,
  isProgressFinding,
} from './evidenceSummary.ts';

export interface DiscoveryActivityEntry {
  id: string;
  /** ISO timestamp the stream is ordered by; entries without one sort last. */
  at?: string;
  time: string;
  glyph: string;
  actor: string;
  text: string;
  tone?: 'info' | 'ok' | 'warn' | 'muted' | 'you';
  ref?: string;
  isReasoning?: boolean;
  pending?: boolean;
  reasoningLabel?: string;
  /** Rendered whole (never typed out) — e.g. a large batch planning block. */
  live?: boolean;
  /** A reasoning block the operator can expand: collapsed to a label + toggle by default. */
  collapsible?: boolean;
  /** Replace status snapshots in place instead of letting polling pin copies to the tail. */
  replaceInPlace?: boolean;
}

export function formatDiscoveryClock(iso?: string): string {
  if (!iso) return '—';
  const stamp = iso.slice(11, 19);
  return stamp || iso;
}

function clock(iso?: string): string {
  return formatDiscoveryClock(iso);
}

export function releasedCapabilityLabels(methods?: readonly string[]): string {
  const labels: string[] = [];
  if (methods?.includes('DNS_QUERY')) labels.push('DNS queries');
  if (methods?.includes('NETWORK_PROBE')) labels.push('Network probes');
  return labels.join(', ') || 'None listed';
}

export function scopeSummaryText(
  scope: { scopeDomains?: string[]; scopeIpAddresses?: string[] } | null | undefined,
  fallback: string,
): string {
  return authorizedScopeSummary(scope, fallback);
}

const DEFAULT_UNIT: { glyph: string; tone?: DiscoveryActivityEntry['tone']; verb: string } = {
  glyph: '›',
  verb: 'running',
};
const UNIT_STATUS: Record<
  string,
  { glyph: string; tone?: DiscoveryActivityEntry['tone']; verb: string }
> = {
  NOT_ATTEMPTED: { glyph: '·', tone: 'muted', verb: 'queued' },
  RUNNING: { glyph: '›', verb: 'running' },
  COMPLETE: { glyph: '✓', tone: 'ok', verb: 'completed' },
  FAILED: { glyph: '✕', tone: 'warn', verb: 'failed' },
  PARTIAL: { glyph: '!', tone: 'warn', verb: 'partial' },
  SKIPPED: { glyph: '·', tone: 'muted', verb: 'skipped' },
};

function isNetworkUnit(unit: {
  targetType?: string;
  targetKind?: string;
  method?: string;
}): boolean {
  if (unit.method === 'NETWORK_PROBE') return true;
  if (unit.method === 'DNS_QUERY') return false;
  return unit.targetType === 'IP_ADDRESS' || unit.targetKind === 'IP_ADDRESS';
}

export function unitTaskLabel(unit: {
  targetType?: string;
  targetKind?: string;
  method?: string;
}): string {
  if (unit.method === 'DNS_QUERY') return 'DNS query';
  if (unit.method === 'NETWORK_PROBE') return 'network probe';
  return isNetworkUnit(unit) ? 'network probe' : 'DNS query';
}

export function coverageCountText(summary: DiscoveryProgressResponse['summary']): string {
  const parts = [
    `${summary.completedUnits} processed`,
    `${summary.failedUnits} failed`,
    `${summary.skippedUnits} skipped`,
  ];
  if (summary.partialUnits > 0) parts.push(`${summary.partialUnits} partial`);
  return parts.join(', ');
}

export function cleanupStatusText(cleanup: {
  state: string;
  details?: string;
  confirmedAt?: string;
  receiptId?: string;
}): string {
  const state =
    cleanup.state === 'NOT_REQUIRED'
      ? 'Not required'
      : cleanup.state === 'CONFIRMED'
        ? 'Confirmed'
        : cleanup.state === 'FAILED'
          ? 'Failed'
          : 'Pending';
  return [state, cleanup.details].filter(Boolean).join(' · ');
}

export function discoveryActivityLog(
  progress: DiscoveryProgressResponse | null | undefined,
  records: DiscoveryRecords | null | undefined,
): DiscoveryActivityEntry[] {
  const entries: DiscoveryActivityEntry[] = [];

  if (progress) {
    const stale = progress.heartbeatState === 'STALE';
    const failed = progress.status === 'FAILED';
    const stopped = progress.status === 'STOPPED';
    entries.push({
      id: `attempt-${progress.attemptId}`,
      at: progress.startedAt ?? progress.lastUsefulProgress,
      time: clock(progress.startedAt ?? progress.lastUsefulProgress),
      glyph: failed
        ? '✕'
        : stopped
          ? '■'
          : stale
            ? '!'
            : progress.status === 'COMPLETE'
              ? '✓'
              : '›',
      actor: 'Runner',
      text: `Discovery ${progress.status.toLowerCase().replaceAll('_', ' ')} · runner ${progress.heartbeatState.toLowerCase().replaceAll('_', ' ')}`,
      tone: failed || stale ? 'warn' : progress.status === 'COMPLETE' ? 'ok' : 'info',
      replaceInPlace: true,
    });

    entries.push({
      id: `coverage-${progress.attemptId}`,
      at: progress.startedAt ?? progress.lastUsefulProgress,
      time: clock(progress.startedAt ?? progress.lastUsefulProgress),
      glyph: '·',
      actor: 'Runner',
      text: coverageCountText(progress.summary),
      tone: progress.summary.failedUnits > 0 ? 'warn' : 'muted',
      replaceInPlace: true,
    });

    for (const blocker of progress.blockers) {
      // A limit on a check that did not fail (e.g. historical sources only) is already on its line.
      const onUnit = progress.units.filter((unit) => unit.limitation === blocker);
      if (onUnit.length > 0 && !onUnit.some((unit) => LIMITED.has(unit.status))) continue;
      entries.push({
        id: `blocker-${progress.attemptId}-${blocker}`,
        at: progress.lastUsefulProgress,
        time: clock(progress.lastUsefulProgress),
        glyph: '!',
        actor: 'Runner',
        text: blocker,
        tone: 'warn',
      });
    }

    const queued = new Map<string, DiscoveryProgressResponse['units']>();
    for (const unit of progress.units) {
      if (unit.status === 'NOT_ATTEMPTED') {
        queued.set(unit.targetValue, [...(queued.get(unit.targetValue) ?? []), unit]);
        continue;
      }
      entries.push(
        ...unitLines(
          `unit-${unit.coverageUnitId}`,
          unit,
          unit.targetValue,
          progress.lastUsefulProgress,
        ),
      );
    }
    // A queued check is the plan, not live work. Anchor it to the attempt start so it reads near
    // the top in one line per host and never re-sorts to the tail as lastUsefulProgress advances
    // (that is what pinned and stacked them); the started and completed lines flow at their own
    // times below. Its own id keeps it apart from the unit's later lines, so neither freezes.
    const queuedAt = progress.startedAt ?? progress.lastUsefulProgress;
    for (const [target, units] of queued) {
      const checks = [...new Set(units.flatMap((unit) => unit.check ?? []))];
      const limits = [...new Set(units.flatMap((unit) => unit.limitation ?? []))];
      const [first] = units;
      const what =
        units.length === 1 && first
          ? (first.check ?? unitTaskLabel(first))
          : `${units.length} checks`;
      entries.push({
        id: `queued-${progress.attemptId}-${target}`,
        at: queuedAt,
        time: clock(queuedAt),
        glyph: '·',
        actor: units.every((unit) => isNetworkUnit(unit)) ? 'Network' : 'DNS',
        text: [`${target} · ${what} queued`, units.length > 1 && checks.join(', '), ...limits]
          .filter(Boolean)
          .join(' · '),
        tone: 'muted',
        replaceInPlace: true,
      });
    }

    if (progress.cleanup && progress.cleanup.state !== 'NOT_REQUIRED') {
      entries.push({
        id: `cleanup-${progress.attemptId}`,
        at: progress.cleanup.confirmedAt ?? progress.finishedAt,
        time: clock(progress.cleanup.confirmedAt ?? progress.finishedAt),
        glyph:
          progress.cleanup.state === 'FAILED'
            ? '✕'
            : progress.cleanup.state === 'CONFIRMED'
              ? '✓'
              : '·',
        actor: 'Runner',
        text: `Cleanup ${progress.cleanup.state.toLowerCase()}${
          progress.cleanup.details ? ` · ${progress.cleanup.details}` : ''
        }`,
        tone:
          progress.cleanup.state === 'FAILED'
            ? 'warn'
            : progress.cleanup.state === 'CONFIRMED'
              ? 'ok'
              : 'muted',
      });
    }

    // The coordinator's planner turns, as decisions rather than deliberation: one line
    // per top-level turn, reading its dispatched tool call and target. Turns with no
    // dispatched call (prose, protocol repairs) stay quiet instead of dumping raw
    // chain-of-thought into the feed.
    for (const activity of progress.agentActivity?.activities ?? []) {
      if (activity.kind !== 'model_turn') continue;
      const decision = activity.decisionSummary?.trim();
      if (!decision) continue;
      entries.push({
        id: `planner-${activity.id}`,
        at: activity.completedAt ?? activity.at,
        time: clock(activity.completedAt ?? activity.at),
        glyph: '·',
        actor: 'Coordinator',
        text: decision,
        tone: 'info',
      });
    }
  }

  const seenUnits = new Set(progress?.units.map((unit) => unit.coverageUnitId) ?? []);
  for (const unit of records?.attempt?.coverage ?? []) {
    if (seenUnits.has(unit.coverageUnitId)) continue;
    entries.push(
      ...unitLines(
        `coverage-${unit.coverageUnitId}`,
        unit,
        unit.target,
        records?.attempt?.startedAt,
      ),
    );
  }

  const coverage = records?.attempt?.coverage ?? [];
  const sourceRecords = records?.sourceRecords ?? [];
  const evidenceRows = distinctEvidence(records?.evidence ?? [], sourceRecords);
  const pathRows = new Map<string, typeof evidenceRows>();
  for (const evidence of evidenceRows) {
    const key = pathKind(evidence.summary);
    if (!key) continue;
    const group = `${evidence.sourceRecordId}:${key}`;
    pathRows.set(group, [...(pathRows.get(group) ?? []), evidence]);
  }
  // A crawl or path probe returns dozens of rows; the log keeps one line, the Evidence page all.
  const grouped = new Set<string>();
  for (const [group, rows] of pathRows) {
    const [first] = rows;
    if (rows.length <= PATH_ROWS_SHOWN || !first) continue;
    for (const row of rows) grouped.add(row.evidenceId);
    const statuses = new Map<string, number>();
    for (const row of rows) {
      const status = /HTTP (\d{3}[a-z -]*)$/.exec(row.summary ?? '')?.[1] ?? 'no status';
      statuses.set(status, (statuses.get(status) ?? 0) + 1);
    }
    const host = sourceRecords.find(
      (item) => item.sourceRecordId === first.sourceRecordId,
    )?.subject;
    entries.push({
      id: `evidence-group-${group}`,
      at: first.capturedAt,
      time: clock(first.capturedAt),
      glyph: '‹',
      actor: 'Network',
      text: [
        host,
        `${rows.length} ${pathKind(first.summary)}`,
        [...statuses].map(([status, count]) => `${status} ×${count}`).join(', '),
        'each listed on the Evidence page',
      ]
        .filter(Boolean)
        .join(' · '),
      tone: 'muted',
    });
  }
  for (const evidence of evidenceRows) {
    if (grouped.has(evidence.evidenceId)) continue;
    const parent = coverageForEvidence(coverage, sourceRecords, evidence.sourceRecordId);
    const network =
      evidence.kind === 'NETWORK_ENDPOINT' ||
      parent?.method === 'NETWORK_PROBE' ||
      parent?.targetKind === 'IP_ADDRESS';
    const error = evidence.kind === 'ERROR_RECORD';
    entries.push({
      id: `evidence-${evidence.evidenceId}`,
      at: evidence.capturedAt,
      time: clock(evidence.capturedAt),
      glyph: error ? '✕' : '‹',
      actor: network ? 'Network' : 'DNS',
      text: withHost(
        formatEvidenceLine(evidence.kind, evidence.summary),
        sourceRecords.find((item) => item.sourceRecordId === evidence.sourceRecordId)?.subject,
      ),
      tone: error ? 'warn' : 'muted',
      ref: shortId(evidence.evidenceId),
    });
  }

  const progressFindingById = new Map(
    (progress?.findings ?? [])
      .filter(isProgressFinding)
      .map((finding) => [findingId(finding), finding]),
  );
  for (const finding of records?.findings ?? []) {
    const projected = progressFindingById.get(findingId(finding));
    const lane = findingTaskLane(
      {
        ...finding,
        lane: finding.lane ?? projected?.lane,
      },
      records?.evidence ?? [],
    );
    entries.push({
      ...findingLine(finding.normalizedSeverity),
      id: `finding-${findingId(finding)}`,
      at: finding.lastObservedAt ?? finding.firstObservedAt,
      time: clock(finding.lastObservedAt ?? finding.firstObservedAt),
      actor: lane,
      text: findingTitle(finding),
    });
  }

  const seenFindings = new Set((records?.findings ?? []).map((finding) => findingId(finding)));
  for (const finding of progress?.findings ?? []) {
    if (!isProgressFinding(finding) || seenFindings.has(findingId(finding))) continue;
    entries.push({
      ...findingLine(finding.normalizedSeverity),
      id: `finding-${findingId(finding)}`,
      at: progress?.lastUsefulProgress ?? progress?.startedAt,
      time: clock(progress?.lastUsefulProgress ?? progress?.startedAt),
      actor: finding.lane,
      text: findingTitle(finding),
    });
  }

  return byTime(entries);
}

const LIMITED = new Set(['FAILED', 'PARTIAL', 'SKIPPED']);
const PATH_ROWS_SHOWN = 5;

/** The backend's plain wording for one crawled or probed web path, as a plural noun. */
function pathKind(summary?: string): string | undefined {
  if (summary?.startsWith('Linked ')) return 'linked pages found';
  if (summary?.startsWith('Common path ')) return 'common paths checked';
  return undefined;
}

/** "www.aster.example · A 192.0.2.1": a DNS answer names no host, and several hosts share one. */
function withHost(line: string, host?: string): string {
  return host && !line.includes(host) ? `${host} · ${line}` : line;
}

/**
 * A finished unit reads as two lines, started and finished, from its own timestamps, so a scan
 * shorter than the poll interval still shows its whole run; a queued or running unit is one line.
 */
function unitLines(
  id: string,
  unit: {
    status: string;
    startedAt?: string;
    finishedAt?: string;
    limitation?: string;
    targetType?: string;
    targetKind?: string;
    method?: string;
    check?: string;
    reason?: string;
  },
  target: string,
  fallbackAt?: string,
): DiscoveryActivityEntry[] {
  const meta = UNIT_STATUS[unit.status] ?? DEFAULT_UNIT;
  const actor = isNetworkUnit(unit) ? 'Network' : 'DNS';
  const task = `${target} · ${unit.check ?? unitTaskLabel(unit)}`;
  // Why a check runs belongs where it starts: the start line, or the line of a running check.
  const why = unit.reason ? ` · ${unit.reason}` : '';
  const at = unit.finishedAt ?? unit.startedAt ?? fallbackAt;
  const lines: DiscoveryActivityEntry[] = [];
  if (unit.startedAt && unit.finishedAt) {
    lines.push({
      id: `${id}-start`,
      at: unit.startedAt,
      time: clock(unit.startedAt),
      glyph: '›',
      actor,
      text: `${task} started${why}`,
      tone: 'muted',
    });
  }
  lines.push({
    id,
    at,
    time: clock(at),
    glyph: meta.glyph,
    actor,
    text: `${task} ${meta.verb}${unit.status === 'RUNNING' ? why : ''}${
      unit.limitation ? ` · ${unit.limitation}` : ''
    }`,
    tone: meta.tone,
    replaceInPlace: unit.status === 'NOT_ATTEMPTED',
  });
  return lines;
}

/** Severe findings need attention (!); every other finding is waiting for a person (?). */
function findingLine(severity?: string): Pick<DiscoveryActivityEntry, 'glyph' | 'tone' | 'ref'> {
  const severe = severity === 'CRITICAL' || severity === 'HIGH';
  return {
    glyph: severe ? '!' : '?',
    tone: severe || severity === 'MEDIUM' ? 'warn' : 'info',
    ref: severity && severity.charAt(0) + severity.slice(1).toLowerCase(),
  };
}

/** Stable chronological order, as a log reads; undated entries stay at the end. */
export function byTime<T extends { at?: string }>(entries: T[]): T[] {
  const key = (entry: T) => {
    const time = entry.at ? Date.parse(entry.at) : NaN;
    return Number.isNaN(time) ? Infinity : time;
  };
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => key(a.entry) - key(b.entry) || a.index - b.index)
    .map(({ entry }) => entry);
}

/** The terminal's status pill for the latest attempt, e.g. "Running · step 3 of 6 · 6/19 units (32%)". */
export function discoveryStatusText(
  status: Parameters<typeof attemptStatusLabel>[0],
  step: number,
  summary?: DiscoveryProgressResponse['summary'],
): string | null {
  if (status !== 'RUNNING') return attemptStatusLabel(status);
  const running = `Running · step ${step} of 6`;
  if (!summary?.totalUnits) return running;
  const done =
    summary.completedUnits + summary.failedUnits + summary.partialUnits + summary.skippedUnits;
  return `${running} · ${done}/${summary.totalUnits} units (${Math.round((done / summary.totalUnits) * 100)}%)`;
}

export function discoveryPipelineStep(
  progress: DiscoveryProgressResponse | null | undefined,
  records: DiscoveryRecords | null | undefined,
): number {
  const status = progress?.status ?? records?.attempt?.status;
  if (!status || status === 'QUEUED') return 1;
  if (status === 'COMPLETE') return 6;
  if (status === 'PARTIAL' || status === 'FAILED' || status === 'STOPPED') return 5;

  const units = progress?.units ?? records?.attempt?.coverage ?? [];
  const hasFinishedDns = units.some(
    (unit) => unit.status === 'COMPLETE' || unit.status === 'PARTIAL',
  );
  const hasNetwork = units.some((unit) => isNetworkUnit(unit));
  if (hasNetwork && hasFinishedDns) return 4;
  if (hasFinishedDns) return 3;
  return 2;
}
